import { createIdempotencyKey } from '../../utils/idempotency.js';
import { translateRuntimeKey } from '../../i18n/runtimeState.js';

const t = (key, fallback, params) =>
    translateRuntimeKey(`abx.prescription.${key}`, fallback, params);

export const ABX_PRESCRIPTION_FIELDS = [
    'localPrescriptionId',
    'prescriberSelection',
    'specificIndication',
    'antibioticEntryId',
    'highRiskBroadSpectrum',
    'routeAtStart',
    'startDate',
    'plannedStopDate',
    'actualStopDate',
    'actualStopDateMode',
    'minimumStartCriteria',
    'indicationDocumented',
    'selectionAdherent',
    'doseAdherent',
    'routeAdherent',
    'durationAdherent',
    'allergyVerified',
    'diagnosticTestOrdered',
    'testingCriteriaMet',
    'diagnosticResultAvailable',
    'timeoutCompletedDate',
    'reviewAction',
    'therapyModifiedAfterResults',
    'antibioticRelatedAdverseEvent',
    'notes',
    'priorPrescriptionId',
];

const TEXT_LIMITS = {
    localPrescriptionId: 191,
    specificIndication: 1000,
    notes: 10000,
};

const PRESCRIBER_NAME_LIMIT = 255;

export const yesNoOptions = [
    { label: t('common.yes', 'Yes'), value: 'YES' },
    { label: t('common.no', 'No'), value: 'NO' },
];

export const yesNoNotApplicableOptions = [
    ...yesNoOptions,
    {
        label: t('common.notApplicable', 'Not applicable'),
        value: 'NOT_APPLICABLE',
    },
];

export const assessmentOptions = [
    ...yesNoNotApplicableOptions,
    {
        label: t('common.unable', 'Unable to determine'),
        value: 'UNABLE_TO_DETERMINE',
    },
];

export const minimumStartCriteriaOptions = [
    ...yesNoOptions,
    {
        label: t('common.unable', 'Unable to determine'),
        value: 'UNABLE_TO_DETERMINE',
    },
];

const routeOption = (labelKey, fallback, value) => ({
    label: t(labelKey, fallback),
    labelKey,
    value,
});

export const routeOptions = [
    routeOption('options.oral', 'Oral', 'ORAL'),
    routeOption('options.intravenous', 'Intravenous', 'INTRAVENOUS'),
    routeOption('options.intramuscular', 'Intramuscular', 'INTRAMUSCULAR'),
    routeOption('options.topical', 'Topical', 'TOPICAL'),
    routeOption('options.inhaled', 'Inhaled', 'INHALED'),
    routeOption('options.other', 'Other', 'OTHER'),
];

// Module labels keep the locale from load time; translate again on each call.
export function localizedRouteOptions() {
    return routeOptions.map((option) => ({
        ...option,
        label: t(option.labelKey, option.label),
    }));
}

export function routeOptionLabel(value) {
    const option = routeOptions.find((item) => item.value === value);
    return option ? t(option.labelKey, option.label) : '';
}

export const reviewActionOptions = [
    {
        label: t('options.continueUnchanged', 'Continue unchanged'),
        value: 'CONTINUE_UNCHANGED',
    },
    { label: t('options.stop', 'Stop'), value: 'STOP' },
    {
        label: t('options.changeAntibiotic', 'Change antibiotic'),
        value: 'CHANGE_ANTIBIOTIC',
    },
    {
        label: t('options.narrow', 'Narrow or de-escalate'),
        value: 'NARROW_DEESCALATE',
    },
    {
        label: t('options.doseAdjusted', 'Dose adjusted'),
        value: 'DOSE_ADJUSTED',
    },
    {
        label: t('options.durationAdjusted', 'Duration adjusted'),
        value: 'DURATION_ADJUSTED',
    },
    { label: t('options.ivToOral', 'IV to oral'), value: 'IV_TO_PO' },
    {
        label: t('options.noChange', 'No change required'),
        value: 'NO_CHANGE_REQUIRED',
    },
    { label: t('options.other', 'Other'), value: 'OTHER' },
    {
        label: t('common.notApplicable', 'Not applicable'),
        value: 'NOT_APPLICABLE',
    },
];

export const costStateOptions = [
    { label: t('common.notRecorded', 'Not recorded'), value: null },
    { label: t('options.known', 'Known'), value: 'KNOWN' },
    { label: t('options.unavailable', 'Unavailable'), value: 'UNAVAILABLE' },
];

export const companyVisibilityOptions = [
    {
        label: t('options.currentSite', 'Current site only'),
        value: 'SELECTED_SITES',
    },
    {
        label: t('options.allSites', 'All accessible company sites'),
        value: 'ALL_ACCESSIBLE_COMPANY_SITES',
    },
];

function emptyToNull(value) {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized || null;
}

function positiveIdOrNull(value) {
    if (value === null || value === undefined || value === '') return null;
    const normalized = Number(value);
    return Number.isInteger(normalized) && normalized > 0 ? normalized : value;
}

function normalizePrescriberSelection(value) {
    if (!value || typeof value !== 'object') return null;
    if (value.type === 'TEAM') {
        return {
            type: 'TEAM',
            teamMemberId: positiveIdOrNull(value.teamMemberId),
        };
    }
    if (value.type === 'OTHER') {
        return { type: 'OTHER', name: emptyToNull(value.name) };
    }
    return null;
}

export function emptyPrescriptionForm() {
    return {
        ...Object.fromEntries(
            ABX_PRESCRIPTION_FIELDS.map((field) => [field, null])
        ),
        actualStopDateMode: 'DEFAULT',
    };
}

export function prescriptionForm(prescription = null) {
    const fields = prescription?.fields || {};
    const form = Object.fromEntries(
        ABX_PRESCRIPTION_FIELDS.map((field) => [field, fields[field] ?? null])
    );
    form.actualStopDateMode = fields.actualStopDateMode || 'DEFAULT';
    return form;
}

export function normalizePrescriptionDraft(form = {}) {
    const normalized = emptyPrescriptionForm();
    for (const field of ABX_PRESCRIPTION_FIELDS) {
        const value = form[field];
        if (Object.hasOwn(TEXT_LIMITS, field)) {
            normalized[field] = emptyToNull(value);
        } else if (
            field === 'antibioticEntryId' ||
            field === 'priorPrescriptionId'
        ) {
            normalized[field] = positiveIdOrNull(value);
        } else if (field === 'prescriberSelection') {
            normalized[field] = normalizePrescriberSelection(value);
        } else {
            normalized[field] =
                value === '' || value === undefined ? null : value;
        }
    }
    normalized.actualStopDateMode =
        normalized.actualStopDateMode === 'OVERRIDE' ? 'OVERRIDE' : 'DEFAULT';
    if (normalized.actualStopDateMode === 'DEFAULT') {
        normalized.actualStopDate = null;
    }
    return normalized;
}

export function prescriptionDraftPatch(form, baseline) {
    const current = normalizePrescriptionDraft(form);
    const previous = normalizePrescriptionDraft(baseline);
    return Object.fromEntries(
        ABX_PRESCRIPTION_FIELDS.filter(
            (field) =>
                JSON.stringify(current[field]) !==
                JSON.stringify(previous[field])
        ).map((field) => [field, current[field]])
    );
}

export function validatePrescriptionDraft(
    form = {},
    { requiredFields = [] } = {}
) {
    const value = normalizePrescriptionDraft(form);
    const errors = {};

    for (const field of requiredFields) {
        if (
            value[field] === null ||
            value[field] === '' ||
            (field === 'prescriberSelection' &&
                (!value[field]?.type ||
                    (value[field].type === 'TEAM' &&
                        !Number.isInteger(value[field].teamMemberId)) ||
                    (value[field].type === 'OTHER' && !value[field].name)))
        ) {
            errors[field] =
                field === 'prescriberSelection'
                    ? t(
                          'validation.prescriberRequired',
                          'Select a Team member or enter the name of the person who prescribed it.'
                      )
                    : t('validation.required', 'Complete this required field.');
        }
    }

    for (const [field, maximum] of Object.entries(TEXT_LIMITS)) {
        if (value[field] && value[field].length > maximum) {
            errors[field] = t(
                'validation.maxLength',
                `Use ${maximum} characters or fewer.`,
                { maximum }
            );
        }
    }

    if (
        value.startDate &&
        value.plannedStopDate &&
        value.plannedStopDate < value.startDate
    ) {
        errors.plannedStopDate = t(
            'validation.plannedDate',
            'Planned stop date must be on or after Start date.'
        );
    }
    if (
        value.startDate &&
        value.actualStopDate &&
        value.actualStopDate < value.startDate
    ) {
        errors.actualStopDate = t(
            'validation.actualDate',
            'Actual stop date must be on or after Start date.'
        );
    }
    if (
        value.prescriberSelection?.type === 'OTHER' &&
        value.prescriberSelection.name?.length > PRESCRIBER_NAME_LIMIT
    ) {
        errors.prescriberSelection = t(
            'validation.maxLength',
            `Use ${PRESCRIBER_NAME_LIMIT} characters or fewer.`,
            { maximum: PRESCRIBER_NAME_LIMIT }
        );
    }
    if (value.actualStopDateMode === 'OVERRIDE' && !value.actualStopDate) {
        errors.actualStopDate = t(
            'validation.overrideDate',
            'Enter an Actual stop date or use the automatic date.'
        );
    }
    for (const field of ['antibioticEntryId', 'priorPrescriptionId']) {
        if (
            value[field] !== null &&
            (!Number.isInteger(value[field]) || value[field] < 1)
        ) {
            errors[field] = t(
                'validation.selectValue',
                'Select an available prescription value.'
            );
        }
    }

    return { valid: Object.keys(errors).length === 0, value, errors };
}

function issueField(issue) {
    const path = Array.isArray(issue?.path) ? issue.path : [];
    return [...path]
        .reverse()
        .find((segment) => ABX_PRESCRIPTION_FIELDS.includes(segment));
}

export function prescriptionValidationErrors(error) {
    const errors = {};
    for (const issue of error?.details?.issues || []) {
        const field = issueField(issue);
        if (field)
            errors[field] =
                issue.message ||
                t('validation.checkValue', 'Check this value.');
    }
    for (const field of error?.details?.fields || []) {
        if (ABX_PRESCRIPTION_FIELDS.includes(field) && !errors[field]) {
            errors[field] = t('validation.checkValue', 'Check this value.');
        }
    }
    if (error?.code === 'ABX_INVALID_DATE_ORDER') {
        errors.plannedStopDate = t(
            'validation.stopDates',
            'Stop dates must be on or after Start date.'
        );
        errors.actualStopDate = t(
            'validation.stopDates',
            'Stop dates must be on or after Start date.'
        );
    }
    if (error?.code === 'ABX_PRESCRIBER_NOT_ELIGIBLE') {
        errors.prescriberSelection = t(
            'validation.prescriberUnavailable',
            'This Team member is no longer available for this site. Select someone else or enter a name.'
        );
    }
    if (
        error?.status === 422 &&
        error?.code === 'ABX_VALIDATION_FAILED' &&
        !Object.keys(errors).length
    ) {
        errors.priorPrescriptionId = t(
            'validation.reference',
            'That prescription or antibiotic cannot be used here.'
        );
    }
    return errors;
}

export function prescriptionErrorMessage(error, operation = 'save') {
    if (error?.status === 409) {
        if (error.code === 'ABX_IDEMPOTENCY_MISMATCH') {
            return t(
                'errors.idempotencyMismatch',
                'This retry differs from the original create request. Review the draft before trying again.'
            );
        }
        if (error.code === 'ABX_IDEMPOTENCY_IN_PROGRESS') {
            return t(
                'errors.processing',
                'The original create request is still processing. Wait before retrying.'
            );
        }
        return t(
            'errors.conflict',
            'Someone else updated this prescription. Refresh and review the latest version before saving again.'
        );
    }
    if (error?.status === 403) {
        return t(
            'errors.permission',
            'You no longer have permission to change this prescription. Your entries have been kept.'
        );
    }
    if (error?.status === 422 || error?.status === 400) {
        return t(
            'errors.validation',
            'Check the highlighted prescription fields.'
        );
    }
    if (error?.status === 404) {
        return t(
            'errors.notFound',
            'This prescription is no longer available.'
        );
    }
    if (error?.code === 'ABX_CATALOGUE_UNAVAILABLE') {
        return t(
            'errors.catalogue',
            'The antibiotic catalogue is unavailable. Your other entries have been kept.'
        );
    }
    return operation === 'create'
        ? t(
              'errors.create',
              'The prescription draft could not be created. Your entries have been kept.'
          )
        : t(
              'errors.save',
              'The prescription draft could not be saved. Your entries have been kept.'
          );
}

export function parseAbxEtag(etag, fallback = null) {
    const match = String(etag || '').match(/^"([1-9]\d*)"$/);
    if (match) return Number(match[1]);
    return Number.isInteger(fallback) && fallback > 0 ? fallback : null;
}

export function createPrescriptionIdempotencyKey() {
    return createIdempotencyKey('abx-prescription-create');
}

export function emptyCompanyAntibioticForm(siteId) {
    return {
        name: '',
        ingredients: [{ text: '' }],
        routeForm: '',
        reason: '',
        visibilityScope: 'SELECTED_SITES',
        siteId: Number(siteId),
    };
}

export function validateCompanyAntibiotic(form = {}) {
    const ingredients = (form.ingredients || [])
        .map((item) => emptyToNull(item?.text ?? item))
        .filter(Boolean);
    const value = {
        name: emptyToNull(form.name),
        ingredients,
        routeForm: emptyToNull(form.routeForm),
        reason: emptyToNull(form.reason),
        visibility: {
            scope: form.visibilityScope,
            siteIds:
                form.visibilityScope === 'SELECTED_SITES'
                    ? [Number(form.siteId)]
                    : [],
        },
    };
    const errors = {};
    if (!value.name)
        errors.name = t(
            'validation.antibioticName',
            'Enter an antibiotic name.'
        );
    else if (value.name.length > 255)
        errors.name = t(
            'validation.maxLength',
            'Use 255 characters or fewer.',
            { maximum: 255 }
        );
    if (!ingredients.length)
        errors.ingredients = t(
            'validation.ingredient',
            'Enter at least one ingredient.'
        );
    if (!value.reason)
        errors.reason = t('validation.reason', 'Enter a reason.');
    else if (value.reason.length > 1000)
        errors.reason = t(
            'validation.maxLength',
            'Use 1000 characters or fewer.',
            { maximum: 1000 }
        );
    if (value.routeForm && value.routeForm.length > 255)
        errors.routeForm = t(
            'validation.maxLength',
            'Use 255 characters or fewer.',
            { maximum: 255 }
        );
    if (
        value.visibility.scope === 'SELECTED_SITES' &&
        (!Number.isInteger(value.visibility.siteIds[0]) ||
            value.visibility.siteIds[0] < 1)
    ) {
        errors.visibilityScope = t(
            'validation.currentSite',
            'A current authorized site is required.'
        );
    }
    return { valid: Object.keys(errors).length === 0, value, errors };
}

export function companyAntibioticErrorMessage(error) {
    if (error?.code === 'ABX_ANTIBIOTIC_NAME_CONFLICT') {
        return t(
            'errors.nameConflict',
            'An active company antibiotic already uses this name. Search the catalogue again.'
        );
    }
    if (error?.status === 403) {
        return t(
            'errors.companyPermission',
            'Company owner or administrator permission is required.'
        );
    }
    if (error?.status === 422 || error?.status === 400) {
        return t(
            'errors.companyValidation',
            'Check the highlighted company antibiotic fields.'
        );
    }
    return t(
        'errors.companyCreate',
        'The company antibiotic could not be added. Your entries have been kept.'
    );
}

export function prescriptionDisplay(prescription = {}) {
    const fields = prescription.fields || {};
    const startDate =
        fields.startDate || t('common.notRecorded', 'Not recorded');
    const stopDate =
        fields.actualStopDate ||
        fields.plannedStopDate ||
        t('common.notRecorded', 'Not recorded');
    return {
        id: prescription.id,
        status: prescription.status,
        statusLabel:
            prescription.status === 'DRAFT'
                ? t('editor.inProgress', 'In progress')
                : prescription.status === 'FINAL'
                ? t('read.finalized', 'Finalized')
                : prescription.status === 'ABANDONED'
                ? t('read.discarded', 'Discarded')
                : t('read.voided', 'Voided'),
        antibiotic:
            prescription.selectedAntibiotic?.displayName ||
            t('editor.antibioticMissing', 'Antibiotic not selected'),
        prescriber:
            prescription.review?.prescriberSnapshot?.displayName ||
            (fields.prescriberSelection?.type === 'OTHER'
                ? fields.prescriberSelection.name
                : null) ||
            fields.prescriber ||
            t('common.notRecorded', 'Not recorded'),
        route:
            routeOptionLabel(fields.routeAtStart) ||
            t('common.notRecorded', 'Not recorded'),
        course: t('list.courseDates', '{start} – {stop}', {
            start: startDate,
            stop: stopDate,
        }),
        startDate,
        plannedStopDate:
            fields.plannedStopDate || t('common.notRecorded', 'Not recorded'),
        updatedAt: prescription.updatedAt || null,
    };
}
